<template>
  <div :class="$style.footer">
    <div v-if="!isOnePOOffer" :class="$style.footerCell" :style="{ width: '20%', fontWeight: 'lighter' }">
      <InputText v-model.trim="productTitle" placeholder="Title" :class="$style.titleInput" />
    </div>
    <div :class="$style.footerCell" :style="{ width: !isOnePOOffer ? '25%' : '40%' }">
      <div :style="{ width: '100%' }" @click="toggleOverlayPanel">
        <RewardsInput :reward-items="rewardItems" />
      </div>
      <Popover ref="overlayPanel">
        <ItemsBundlesList :reward-items="rewardItems" :show-confirmation="false" @save="handleItemsSave" />
      </Popover>
    </div>
    <div :class="$style.footerCell" :style="{ width: !isOnePOOffer ? '30%' : '50%' }">
      <PriceTypeDropdown
        :key="`${shouldResetOfferPricesValues}`"
        :selected-price-type="selectedPriceType"
        :disabled="rewardItems.length === 0"
        :non-consumable-ids="nonConsumableIds"
        @update-price-type="handlePriceTypeChange"
        @update-iap-id="handleIapIdChange"
        @update-rv-amount="handleRvAmountChange"
        @update-in-game-price="handleInGamePriceChange"
      />
    </div>
    <div :class="$style.footerCell" :style="{ width: '15%' }">
      <TagsManager
        :model-value="productLabels"
        :game-id="appId"
        :version-id="versionId"
        :max-visible-tags="2"
        :type="'SHOP_ITEM'"
        @update:model-value="handleTagUpdate"
      />
    </div>
    <div :class="$style.footerCell" :style="{ width: '10%' }">
      <Button icon="pi pi-times" severity="secondary" outlined :class="$style.button" @click="resetForm" />
      <Button icon="pi pi-check" :disabled="!shouldEnableAddButton" :class="$style.button" @click="addReward" />
    </div>
  </div>
</template>

<script setup lang="ts">
import Button from "primevue/button";
import InputText from "primevue/inputtext";
import Popover from "primevue/popover";
import { computed, reactive, ref } from "vue";
import { useRoute } from "vue-router";

import TagsManager from "@/components/TagsManager/TagsManager.vue";
import { OfferProductInput, OfferType, SellableProductPriceType } from "@/graphql";
import ItemsBundlesList from "@/modules/ItemsBundles";
import { FormattedItem } from "@/modules/Rewards/types";
import PriceTypeDropdown from "../components/PriceTypeDropdown/PriceTypeDropdown.vue";
import RewardsInput from "../components/RewardsInput.vue";

export type OfferProductTag = {
  id: string;
  name: string;
};

const emit = defineEmits(["add-reward", "reset-form", "update-price-type"]);

const props = defineProps({
  offerType: {
    type: String,
    required: true,
  },
  appId: {
    type: String,
    required: true,
  },
  versionId: {
    type: String,
    required: true,
  },
  currentProductsCount: {
    type: Number,
    required: true,
  },
});

const isOnePOOffer = computed(() => props.offerType === OfferType.OnePo);
const route = useRoute();

const versionId = ref<string>(route.params.versionId as string);
const iapIds = reactive({ iosId: "", androidId: "" });
const RVAmount = reactive({ ios: 1, android: 1 });
const inGamePrice = reactive({ ios: { itemId: "", quantity: 1 }, android: { itemId: "", quantity: 1 } });
const rewardItems = ref<FormattedItem[]>([]);
const selectedPriceType = ref(SellableProductPriceType.Iap);
const productTitle = ref("");
const productLabels = ref<OfferProductTag[]>([]);
const nonConsumableIds = ref<string[]>([]);

const shouldEnableAddButton = computed(() => {
  if (!productTitle.value && !isOnePOOffer.value) {
    return false;
  }

  switch (selectedPriceType.value) {
    case SellableProductPriceType.Iap:
      return iapIds.iosId && iapIds.androidId && rewardItems.value.length > 0;
    case SellableProductPriceType.Rv:
      return RVAmount.ios && RVAmount.android && rewardItems.value.length > 0;
    case SellableProductPriceType.InGameCurrency:
      return inGamePrice.ios.itemId && inGamePrice.android.itemId && rewardItems.value.length > 0;
    case SellableProductPriceType.Free:
      return rewardItems.value.length > 0;
    default:
      return false;
  }
});

const overlayPanel = ref<InstanceType<typeof Popover> | null>(null);

const toggleOverlayPanel = (event: MouseEvent) => {
  overlayPanel.value?.toggle(event);
};

const handleItemsSave = (items: FormattedItem[]) => {
  rewardItems.value = items;

  const hasSelectedNonConsumables = items.some((item) => item.selectedItem?.type === "NON_CONSUMABLE");
  if (hasSelectedNonConsumables) {
    nonConsumableIds.value = items
      .filter((item) => item.selectedItem?.type === "NON_CONSUMABLE")
      .map((item) => item.selectedItem?.id || "");
  } else {
    nonConsumableIds.value = [];
  }

  overlayPanel.value?.hide();
};

interface IapIds {
  ios: string;
  android: string;
}

const handleIapIdChange = (ids: IapIds) => {
  iapIds.iosId = ids.ios;
  iapIds.androidId = ids.android;
};

interface RvPrices {
  ios: number;
  android: number;
}

const handleRvAmountChange = (prices: RvPrices) => {
  RVAmount.ios = prices.ios;
  RVAmount.android = prices.android;
};

interface Price {
  quantity: number;
  itemId: string;
}

interface InGameCurrencyPrices {
  ios: Price;
  android: Price;
}

const handleInGamePriceChange = (prices: InGameCurrencyPrices) => {
  inGamePrice.ios = prices.ios;
  inGamePrice.android = prices.android;
};

const resetPriceTypeValues = () => {
  iapIds.iosId = "";
  iapIds.androidId = "";
  RVAmount.ios = 1;
  RVAmount.android = 1;
  inGamePrice.ios = { itemId: "", quantity: 1 };
  inGamePrice.android = { itemId: "", quantity: 1 };
};

const handlePriceTypeChange = (value: SellableProductPriceType) => {
  selectedPriceType.value = value;
  resetPriceTypeValues();
};

const shouldResetOfferPricesValues = ref(false);

const resetForm = () => {
  rewardItems.value = [];
  productTitle.value = "";
  productLabels.value = [];
  resetPriceTypeValues();
  selectedPriceType.value = SellableProductPriceType.Iap;
  shouldResetOfferPricesValues.value = !shouldResetOfferPricesValues.value;
};

const addReward = () => {
  try {
    const input: OfferProductInput = {
      rank: props.currentProductsCount + 1,
      priceType: SellableProductPriceType.Iap,
      iosPrice: {},
      androidPrice: {},
      title: productTitle.value,
      items: rewardItems.value.map((item) => ({
        itemId: item.selectedItem?.id || "",
        quantity: item.quantity,
      })),
      tags: productLabels.value.map((tag) => ({
        id: tag.id,
        name: tag.name,
      })),
    };

    switch (selectedPriceType.value) {
      case SellableProductPriceType.Iap:
        input.iosPrice = { IAPId: iapIds.iosId };
        input.androidPrice = { IAPId: iapIds.androidId };
        break;
      case SellableProductPriceType.Rv:
        input.iosPrice = { rvQuantity: RVAmount.ios };
        input.androidPrice = { rvQuantity: RVAmount.android };
        input.priceType = SellableProductPriceType.Rv;
        break;
      case SellableProductPriceType.InGameCurrency:
        input.iosPrice = { inGamePrice: inGamePrice.ios };
        input.androidPrice = { inGamePrice: inGamePrice.android };
        input.priceType = SellableProductPriceType.InGameCurrency;
        break;
      case SellableProductPriceType.Free:
        input.priceType = SellableProductPriceType.Free;
        break;
      default:
        break;
    }

    emit("add-reward", input);
    resetForm();
  } catch (error) {
    console.error("Error adding reward:", error);
  }
};

function handleTagUpdate(tags: OfferProductTag[]) {
  productLabels.value = [...tags];
}
</script>

<style module lang="scss">
.titleInput {
  width: 100%;
  font-size: 12px;
  height: 32px;
}

.footer {
  display: flex;
  width: 100%;
}

.footerCell {
  display: flex;
  align-items: center;
  padding: 0.5rem;
}

.button {
  padding: 0.45rem 0;

  &:first-child {
    margin-right: 0.5rem;
  }
}
</style>
